import React, { useState } from 'react';
import { useStore } from '../store/useStore';
import { Loader2, Briefcase, Building2, Check, AlertCircle } from 'lucide-react';

const OnboardingModal: React.FC = () => {
  const { completeOnboarding } = useStore();
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!role || !company) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Simulate a brief delay for UX
      await new Promise(resolve => setTimeout(resolve, 800));
      await completeOnboarding(role, company);
      // Success - modal will close via store state update
    } catch (e: any) {
      console.error("Onboarding error:", e);
      if (e.code === 'permission-denied') {
        setError("Permission denied. Please refresh and try again.");
      } else {
        setError("Failed to save profile. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />
      
      <div className="relative w-full max-w-md bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl p-8 animate-in zoom-in-95 duration-300">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-indigo-500/10 rounded-xl flex items-center justify-center text-indigo-400 mx-auto mb-4 border border-indigo-500/20">
            <Briefcase size={24} />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Welcome to Idea Shelf</h2>
          <p className="text-zinc-400 text-sm">Tell us a bit about yourself to personalize your experience.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider ml-1">What is your role?</label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
              <input 
                type="text" 
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="e.g. Founder, Product Manager, Student"
                className="w-full bg-black/40 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-zinc-600 focus:border-indigo-500/50 outline-none transition-colors"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider ml-1">What are you building for?</label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
              <input 
                type="text" 
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="e.g. My Startup, Company Inc., Side Project"
                className="w-full bg-black/40 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-zinc-600 focus:border-indigo-500/50 outline-none transition-colors"
                required
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-xs">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <button 
            type="submit" 
            disabled={!role || !company || isSubmitting}
            className="w-full py-3 bg-white text-black font-semibold rounded-xl hover:bg-zinc-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-4 flex items-center justify-center gap-2"
          >
            {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
            Get Started
          </button>
        </form>
      </div>
    </div>
  );
};

export default OnboardingModal;